import { useEffect, useRef, useState } from "react";

export const CustomCursor = () => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const target = useRef({ x: -100, y: -100 });
  const current = useRef({ x: -100, y: -100 });
  const lastLabel = useRef("");
  const lastHover = useRef(false);
  const rafId = useRef(0);
  const [label, setLabel] = useState("");

  useEffect(() => {
    // Skip on touch / coarse-pointer devices entirely
    if (typeof window !== "undefined" && window.matchMedia("(hover: none), (pointer: coarse)").matches) {
      return undefined;
    }

    const dot = dotRef.current;
    const ring = ringRef.current;

    const handleMove = (e) => {
      target.current.x = e.clientX;
      target.current.y = e.clientY;
      // Dot follows the mouse 1:1 — write transform directly, no CSS transition.
      // Using translate3d engages the GPU compositor.
      if (dot) {
        dot.style.transform = `translate3d(${e.clientX - 4}px, ${e.clientY - 4}px, 0)`;
      }
    };

    const handleOver = (e) => {
      const el = e.target.closest && e.target.closest("[data-cursor]");
      const isHover = !!el;
      // Only touch the DOM / React state when the value actually changes
      if (isHover !== lastHover.current) {
        lastHover.current = isHover;
        if (ring) ring.classList.toggle("hover", isHover);
      }
      const nextLabel = isHover ? (el.getAttribute("data-cursor") || "") : "";
      if (nextLabel !== lastLabel.current) {
        lastLabel.current = nextLabel;
        setLabel(nextLabel);
      }
    };

    const setHidden = (hidden) => {
      if (dot) dot.classList.toggle("hidden", hidden);
      if (ring) ring.classList.toggle("hidden", hidden);
    };
    const onLeave = () => setHidden(true);
    const onEnter = () => setHidden(false);

    const tick = () => {
      // Smooth lerp for the ring (heavier element)
      current.current.x += (target.current.x - current.current.x) * 0.22;
      current.current.y += (target.current.y - current.current.y) * 0.22;
      if (ring) {
        ring.style.transform = `translate3d(${current.current.x - 18}px, ${current.current.y - 18}px, 0)`;
      }
      rafId.current = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", handleMove, { passive: true });
    window.addEventListener("mouseover", handleOver, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);
    rafId.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseover", handleOver);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      cancelAnimationFrame(rafId.current);
    };
  }, []);

  return (
    <>
      <div ref={dotRef} className="cursor-dot" />
      <div ref={ringRef} className="cursor-ring">
        <span className="cursor-label">{label}</span>
      </div>
    </>
  );
};

export default CustomCursor;
